<template>
  <div class="cf-page-container">
    <div class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-16">
      <h1 class="cf-title cf-title--1">
        Send a Gift Card
      </h1>

      <cf-validated-form name="giftCardPurchase">
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <cf-gift-card-amount-select
            v-model="amount"
            label="Gift Card Amount"
            :amounts="merchant.giftCardConfiguration.amounts.map(({ amount }) => amount)"
            :min-value="merchant.giftCardConfiguration.minimumReloadAmount"
            :max-value="merchant.giftCardConfiguration.maximumCardAmount"
          />

          <cf-gift-card-design-select v-model="cardDesignId" />

          <cf-radio-input
            v-model="sendMethod"
            name="sendMethod"
            radio-style="box"
            label="Send As"
            :options="[
              { display: 'Email', value: 'email' },
              { display: 'Text', value: 'phone' }
            ]"
            :validations="{
              required: true
            }"
          />

          <cf-radio-input
            v-model="sendGiftCardNow"
            name="sendGiftCardNow"
            radio-style="box"
            label="Schedule Delivery"
            :options="[
              { display: 'Now', value: true },
              { display: 'Send Later', value: false }
            ]"
          />

          <cf-date-picker
            v-if="!sendGiftCardNow"
            v-model="giftCardDeliveryDate"
            :max-date="maxGiftCardPurchaseDate"
            class="spacing"
          />

          <!-- Receipient Info -->
          <div class="cf-flex cf-flex--column cf-flex--gap-12">
            <h3 class="cf-title cf-title--3">
              Recipient Info
            </h3>

            <p class="cf-subtitle cf-subtitle--2">
              We'll send the gift card to the {{ sendMethod === 'phone' ? 'phone number' : 'email address' }} below. You can include an optional message.
            </p>

            <div class="cf-flex cf-flex--column cf-flex--gap-10">
              <cf-text-input
                v-model="recipientInfo.fullName"
                type="text"
                label="Full Name"
                name="recipientullName"
                icon-class="fa-user-circle"
                :validations="{
                  full_name: true,
                  required: true
                }"
              />

              <cf-text-input
                v-if="sendMethod === 'email'"
                :key="1"
                v-model="recipientInfo.email"
                type="email"
                label="Email Address"
                name="recipientEmail"
                :validations="{
                  required: true
                }"
              />

              <cf-phone-number-input
                v-if="sendMethod === 'phone'"
                :key="2"
                v-model="recipientInfo.primarySmsNumber"
                label="Phone Number"
                name="recipientPhone"
                :validations="{
                  required: true
                }"
              />

              <cf-text-input
                v-model="message"
                type="textarea"
                label="Your Message"
                name="giftingMessage"
                :validations="{
                  max: sendMethod === 'phone' ? 100 : 255
                }"
              />
            </div>
          </div>

          <div class="cf-flex cf-flex--column cf-flex--gap-12">
            <h3 class="cf-title cf-title--3">
              Sender Info
            </h3>

            <div class="cf-flex cf-flex--column cf-flex--gap-10">
              <cf-text-input
                v-model="senderInfo.fullName"
                type="text"
                label="Full Name"
                name="firstNameSender"
                icon-class="fa-user-circle"
                :validations="{
                  full_name: true,
                  required: true
                }"
              />

              <cf-text-input
                v-model="senderInfo.email"
                type="email"
                label="Email Address"
                name="emailSender"
                :validations="{
                  required: true
                }"
              />
            </div>
          </div>

          <div class="cf-flex cf-flex--row cf-flex--justify-center">
            <div :class="['cf-grid cf-grid--gap-10 cf-mobile-column cf-max-width-600', { 'cf-grid--2-grid-column-equal': currentUser.accountId }]">
              <cf-payment-methods
                pay-cta-text="Place Order"
                payment-modal-title="Select Payment Method"
                primary-cta-text="Pay Now"
                :payment-gateway-override="merchant.defaultPaymentGateway"
                :hidden-elements="{
                  contactInfo: true,
                  giftCards: true,
                  roomCharge: true,
                  signIn: true,
                  smsReceipt: true,
                  tendr: true,
                  total: selectedLocation.features.HidePrices
                }"
                :sender-info="senderInfo"
                :total="amount"
                :checkout-handler="checkout"
              >
                <template #payment-modal-top>
                  <div class="cf-flex cf-flex--column cf-flex--gap-16">
                    <h1 class="cf-title cf-title--jumbo cf-align-self-center">
                      {{ giftCardGiftingAmount }}
                    </h1>

                    <div class="cf-flex cf-flex--column cf-flex--gap-10">
                      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
                        <h3 class="cf-title cf-title--3">
                          Recipient Info
                        </h3>

                        <button
                          class="cf-title cf-title--5 cf-title--brand-primary cf-cursor-pointer"
                          type="button"
                          title="Edit Recipient Info"
                          @click="$closeAllModals"
                        >
                          Edit
                        </button>
                      </div>

                      <div class="cf-flex cf-flex--column cf-text-align-left">
                        <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                          {{ recipientInfo.fullName }}
                        </p>

                        <p class="cf-subtitle cf-subtitle--2">
                          {{ recipientSendTypeInfo }}
                        </p>
                      </div>

                      <p
                        v-if="message"
                        class="cf-quote cf-subtitle cf-subtitle--3 cf-text-align-left"
                      >
                        {{ message }}
                      </p>
                    </div>

                    <cf-title-separator :omit-title="true" />

                    <div class="cf-flex cf-flex--column cf-flex--gap-10">
                      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
                        <h3 class="cf-title cf-title--3">
                          Sender Info
                        </h3>
                      </div>

                      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-text-align-left">
                        You'll receive a confirmation to the email below.
                      </p>

                      <div class="cf-flex cf-flex--column cf-text-align-left">
                        <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                          {{ senderInfo.fullName }}
                        </p>

                        <p class="cf-subtitle cf-subtitle--2">
                          {{ senderInfo.email }}
                        </p>
                      </div>
                    </div>
                  </div>
                </template>
              </cf-payment-methods>

              <button
                v-if="currentUser.accountId"
                type="button"
                class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
                @click="backToGiftCardsClickHandler"
              >
                Back to Gift Cards
              </button>
            </div>
          </div>
        </div>
      </cf-validated-form>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment-timezone';

  import dollars from '@/filters/dollars';
  import phone from '@/filters/phone';

  import errorHandlers from '@/services/error-handlers';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfRadioInput from '@/view/components/shared/cf-radio-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';

  import CfGiftCardAmountSelect from '@/view/components/globals/cf-gift-card-amount-select/index.vue';
  import CfGiftCardDesignSelect from '@/view/components/globals/cf-gift-card-design-select/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';
  import CfDatePicker from '@/view/components/globals/cf-date-picker/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';


  export default {
    name: 'SendGiftCardPage',

    components: {
      CfGiftCardAmountSelect,
      CfGiftCardDesignSelect,
      CfValidatedForm,
      CfPhoneNumberInput,
      CfRadioInput,
      CfTextInput,
      CfPaymentMethods,
      CfDatePicker,
      CfTitleSeparator
    },

    metaInfo() {
      return {
        title: 'Send a Gift Card'
      };
    },

    data() {
      return {
        amount: 0,
        message: null,
        cardDesignId: null,
        recipientInfo: {
          email: null,
          fullName: null,
          primarySmsNumber: null
        },
        senderInfo: {
          fullName: null,
          email: null
        },
        sendMethod: 'email',
        sendGiftCardNow: true,
        giftCardDeliveryDate: moment().add(1, 'days').toDate()
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['currentUser']),

      maxGiftCardPurchaseDate() {
        const maxDaysOut = this.merchant.giftCardConfiguration.scheduledSendDaysMax;

        return moment().add(maxDaysOut, 'days').toDate();
      },

      recipientSendTypeInfo() {
        return this.sendMethod === 'phone'
          ? phone(this.recipientInfo.primarySmsNumber)
          : this.recipientInfo.email;
      },

      giftCardGiftingAmount() {
        return dollars(this.amount);
      }
    },

    watch: {
      'currentUser.firstName': 'currentUserWatcher'
    },

    created() {
      // if a user signs in on separate page, then set sender info to current user on created.
      this.onComponentCreated();
    },

    methods: {
      // if a user signs in on actual gifting page, then set sender info to current user on watch changes.
      currentUserWatcher() {
        this.senderInfo.fullName = this.currentUser.fullName;
        this.senderInfo.email = this.currentUser.email;
      },

      onComponentCreated() {
        if (this.currentUser) {
          this.senderInfo.email = this.currentUser.email;
          this.senderInfo.fullName = this.currentUser.fullName;
        }
      },

      async checkout(options) {
        try {
          const recipientInfo = {
            fullName: this.recipientInfo.fullName
          };

          if (this.sendMethod === 'phone') {
            recipientInfo.primarySmsNumber = this.recipientInfo.primarySmsNumber;
          }

          else {
            recipientInfo.email = this.recipientInfo.email;
          }

          const receipt = await this.$store.dispatch('giftCards/purchaseGiftedCard', {
            amount: this.amount,
            cardDesignId: this.cardDesignId,
            contactInfo: options.contactInfo,
            message: this.message,
            paymentGateway: this.merchant.defaultPaymentGateway,
            paymentMethod: options.paymentMethod,
            recipientInfo,
            ...!this.sendGiftCardNow && { deliveryDate: this.giftCardDeliveryDate }
          });

          options.onCheckoutSuccess();

          this.$closeAllModals();

          this.$openModal('giftCardReceipt', { receipt });

          this.$router.push({
            name: this.currentUser.accountId ? 'paymentMethodsGiftCards' : 'orderMenu',
            params: { merchantId: this.$route.params.merchantId }
          });
        }

        catch (error) {
          options.onCheckoutError();
          errorHandlers.generic(error);
        }
      },

      backToGiftCardsClickHandler() {
        this.$router.push({
          name: 'paymentMethodsGiftCards',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      }
    }
  };
</script>
